// @section 1 Command / Today — newspaper front page of the big things that HAPPENED yesterday.
// Retrospective recap (NOT a forward to-do list). Reads the latest complete
// serving.ops_pulse_daily row via GET /api/hq/ops-pulse and templates up to five
// real outcome headlines (revenue-first), with "data-gap" tiles that NAME what is
// too dirty to print. Decisions + model: _docs/optiq-hq/command-today-spec-2026-06-19.md.
// Up to five (print only the real ones), never pad. This page renders; it does not derive.

import { useEffect, useMemo, useState } from 'react';
import { getOpsPulse, type OpsPulseDay } from '../../api';
import WebsiteVisitorsCard from '../../components/WebsiteVisitorsCard';

// @subsection 1.1 Helpers
function usd(v: number | null | undefined): string {
  return v == null ? '$0' : `$${Math.round(v).toLocaleString()}`;
}
function num(v: number | null | undefined): number {
  return typeof v === 'number' ? v : 0;
}
function prettyDate(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

type Tone = 'win' | 'neutral' | 'cold';
type Item = { rank: number; cat: string; headline: string; detail: string; tone: Tone };
type Gap = { cat: string; problem: string };

// Revenue-first ranking of headline categories (objective function).
const CAT_RANK: Record<string, number> = {
  Deals: 1, Bookings: 2, Meetings: 3, Outreach: 4, Shipped: 5,
};

// @subsection 1.2 Build the five + the data gaps from one settled day
function buildPage(day: OpsPulseDay): { five: Item[]; gaps: Gap[] } {
  const items: Item[] = [];
  const gaps: Gap[] = [];

  // 1. Deals (highest signal). Present + non-zero -> headline; absent -> data gap.
  const deals = day.businessOutcomes?.deals;
  if (deals && (num(deals.won) || num(deals.closed) || num(deals.touched))) {
    if (num(deals.won) > 0) {
      items.push({ rank: 0, cat: 'Deals', tone: 'win',
        headline: `${deals.won} deal${num(deals.won) > 1 ? 's' : ''} won${deals.won_value_usd ? ` · ${usd(deals.won_value_usd)}` : ''}`,
        detail: 'A deal closed-won yesterday.' });
    } else if (num(deals.closed) > 0) {
      items.push({ rank: 0, cat: 'Deals', tone: 'neutral',
        headline: `${deals.closed} deal${num(deals.closed) > 1 ? 's' : ''} closed`, detail: 'Pipeline resolved yesterday.' });
    } else {
      items.push({ rank: 0, cat: 'Deals', tone: 'neutral',
        headline: `${deals.touched} deal${num(deals.touched) > 1 ? 's' : ''} advanced`, detail: 'Active pipeline was touched.' });
    }
  } else {
    gaps.push({ cat: 'Deals', problem: 'No deal events captured. The CRM last_activity_at write-path was just wired (2026-06-19), so this should populate going forward.' });
  }

  // 2. Bookings — named, confirmed, non-test.
  const bookings = (day.bookings ?? []).filter(
    (b) => (b.status ?? 'confirmed') !== 'cancelled'
      && !/test/i.test(b.name ?? '') && !/test/i.test(b.company ?? ''),
  );
  for (const b of bookings) {
    items.push({ rank: 0, cat: 'Bookings', tone: 'win',
      headline: `${b.name || 'Someone'} booked a call`,
      detail: [b.company, b.slug, b.start_local].filter(Boolean).join(' · ') });
  }

  // 3. Meetings — attendees aren't captured + rows double, so it can't be verified yet.
  const m = day.businessOutcomes?.meetings;
  gaps.push({
    cat: 'Meetings',
    problem: m && num(m.calendar_events) > 0
      ? `${m.calendar_events} calendar events, but attendees aren't captured (and rows double), so "met with someone" can't be verified.`
      : 'Calendar attendees not captured — can\'t distinguish real meetings from solo focus blocks.',
  });

  // 4. Outreach — outcome-led (replies/booked lead; raw send count is sub-stat).
  const ck = day.campaignKpis ?? {};
  const sent = num(ck.sent), replies = num(ck.replies), booked = num(ck.booked);
  if (sent > 0 || replies > 0 || booked > 0) {
    const tone: Tone = booked > 0 ? 'win' : replies > 0 ? 'neutral' : 'cold';
    const headline = booked > 0
      ? `${booked} call${booked > 1 ? 's' : ''} booked from outreach`
      : replies > 0
        ? `${replies} repl${replies > 1 ? 'ies' : 'y'} from ${sent.toLocaleString()} sends`
        : `${sent.toLocaleString()}-email wave sent, 0 replies`;
    items.push({ rank: 0, cat: 'Outreach', tone,
      headline, detail: `${sent.toLocaleString()} sent · ${replies} replies · ${booked} booked` });
  }

  // 5. Shipped — count-level for now (commit messages + chore-filter come later).
  if (num(day.githubCommits) > 0) {
    items.push({ rank: 0, cat: 'Shipped', tone: 'neutral',
      headline: `${day.githubCommits} commits shipped`,
      detail: 'Across repos. Commit-level detail and chore-filtering coming.' });
  }

  // Initiatives — register is frozen; always a gap until write-back exists.
  gaps.push({ cat: 'Initiatives', problem: 'Initiative register frozen since 2026-04-30 — no movement recorded. Needs write-back wiring.' });

  // Lens classifier broke (100% "other") -> note it as a gap so the context strip stays honest.
  const lens = day.costByLens ?? {};
  const lk = Object.keys(lens);
  if (lk.length === 1 && lk[0] === 'other') {
    gaps.push({ cat: 'Attention', problem: 'Cost-by-lens classifier blanked to 100% "other" for this day — lens breakdown suppressed.' });
  }

  items.sort((a, b) => (CAT_RANK[a.cat] ?? 9) - (CAT_RANK[b.cat] ?? 9));
  const five = items.slice(0, 5).map((it, i) => ({ ...it, rank: i + 1 }));
  return { five, gaps };
}

const TONE_CLASS: Record<Tone, string> = {
  win: 'text-ok',
  neutral: 'text-paper-100',
  cold: 'text-stall',
};

// @section 2 Page component
export default function Today() {
  const [day, setDay] = useState<OpsPulseDay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  // @block 2.1 Load latest settled day (endpoint returns oldest-first; take last)
  useEffect(() => {
    getOpsPulse(3)
      .then((p) => { setDay(p.days?.length ? p.days[p.days.length - 1] : null); setError(null); })
      .catch((e) => setError(String(e)))
      .finally(() => setLoaded(true));
  }, []);

  const page = useMemo(() => (day ? buildPage(day) : null), [day]);

  if (!loaded) return <div className="text-muted text-sm">Loading…</div>;
  if (error) return <div className="rounded-xl border-2 border-stall/40 bg-ink-900 p-4 text-stall text-sm">{error}</div>;
  if (!day || !page) return <div className="text-muted text-sm">No settled day to report yet.</div>;

  const ph = day.pipelineHealth?.verdict ?? 'unknown';
  const phClass = ph === 'ok' ? 'text-ok' : ph === 'failed' ? 'text-stall' : 'text-watch';

  return (
    <div className="max-w-4xl mx-auto">
      {/* @block 2.2 Masthead */}
      <div className="border-b-2 border-paper-100/20 pb-3 mb-6">
        <div className="text-[11px] uppercase tracking-[3px] text-muted">Yesterday in review</div>
        <h1 className="text-3xl md:text-4xl font-bold text-paper-100 tracking-tight">{prettyDate(day.day)}</h1>
      </div>

      {/* @block 2.3 The five (up to five; only real outcomes) */}
      {page.five.length === 0 ? (
        <div className="text-muted text-sm mb-8">No clean outcome events to print for this day.</div>
      ) : (
        <div className="space-y-5 mb-10">
          {page.five.map((it) => (
            <div key={`${it.cat}-${it.rank}`} className="flex gap-4">
              <div className="text-2xl font-bold text-paper-100/25 tabular-nums w-7 shrink-0">{it.rank}</div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[2px] text-muted mb-0.5">{it.cat}</div>
                <div className={`text-xl md:text-2xl font-bold leading-snug ${TONE_CLASS[it.tone]}`}>{it.headline}</div>
                <div className="text-sm text-muted mt-1">{it.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* @block 2.4 Website visitors: site-wide human-vs-scanner, yesterday (self-contained tile) */}
      <WebsiteVisitorsCard />

      {/* @block 2.5 Data gaps — what we couldn't verify, named (doubles as a cleanup nag) */}
      {page.gaps.length > 0 && (
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[2px] text-muted mb-2">Data gaps · can't verify yet</div>
          <div className="space-y-2">
            {page.gaps.map((g) => (
              <div key={g.cat} className="rounded-lg border border-watch/25 bg-watch/5 px-3 py-2 text-xs">
                <span className="text-watch font-semibold">{g.cat}:</span>{' '}
                <span className="text-paper-100/70">{g.problem}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* @block 2.6 Context strip — effort/spend, never a headline */}
      <div className="border-t border-paper-100/10 pt-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-muted">
        <span>pipeline <span className={phClass}>{ph}</span></span>
        <span>spend {usd(day.recordedCostUsd)}</span>
        <span>{num(day.sessions)} sessions</span>
        <span>{num(day.marathonSessions)} marathons</span>
        <span>{num(day.githubCommits)} commits</span>
        {day.gitHygiene?.open_prs != null && <span>{day.gitHygiene.open_prs} open PRs</span>}
      </div>
    </div>
  );
}
